import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { KeyRound, ShieldCheck, UserCircle } from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import Input, { FieldLabel, FieldGroup, FieldHint } from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Avatar from '../../components/ui/Avatar';
import { useAuth } from '../../hooks/useAuth';
import { changeOwnPassword } from '../../utils/api';
import { roleBadge } from '../../utils/commandCenterHelpers';

export default function ProfilePage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const badge = roleBadge(user?.role);

  async function submit(event) {
    event.preventDefault();
    if (!current || !next) {
      toast.error('Fill in all fields');
      return;
    }
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await changeOwnPassword(current, next);
      toast.success('Password changed');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not change password');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="My profile"
        subtitle="Manage your own credentials and review your account details."
        icon={UserCircle}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-5 items-start">
        <Card>
          <CardContent className="flex flex-col items-center text-center py-6">
            <Avatar name={user?.name || '?'} size="lg" className="h-16 w-16 text-[20px]" />
            <p className="mt-3 text-[16px] font-semibold text-slate-900">{user?.name}</p>
            <p className="text-[12.5px] text-slate-500">@{user?.username}</p>
            <Badge variant={badge.variant} size="md" className="mt-2.5">
              <ShieldCheck className="h-3 w-3" /> {badge.label}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-col items-stretch gap-1">
            <div className="flex items-center gap-2">
              <span className="h-7 w-7 rounded-lg bg-brand-50 text-brand-600 inline-flex items-center justify-center">
                <KeyRound className="h-4 w-4" />
              </span>
              <CardTitle>Change password</CardTitle>
            </div>
            <CardDescription>Use a strong password with at least 8 characters, a letter and a number.</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="flex flex-col gap-3.5 max-w-md" onSubmit={submit}>
              <FieldGroup>
                <FieldLabel>Current password</FieldLabel>
                <Input
                  type="password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  autoComplete="current-password"
                  disabled={saving}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>New password</FieldLabel>
                <Input
                  type="password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  autoComplete="new-password"
                  disabled={saving}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Confirm new password</FieldLabel>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  disabled={saving}
                  error={confirm.length > 0 && confirm !== next}
                />
                {confirm.length > 0 && confirm !== next && (
                  <FieldHint tone="error">Passwords do not match</FieldHint>
                )}
              </FieldGroup>
              <Button type="submit" loading={saving} leftIcon={KeyRound} className="mt-1 self-start">
                Update password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
